import isObject from 'lodash/isObject';

import BlueskySvg from '@/assets/images/channels/bluesky.svg';
import FacebookSvg from '@/assets/images/channels/facebook-light.svg';
import GaSvg from '@/assets/images/channels/ga.svg';
import InstagramSvg from '@/assets/images/channels/instagram.svg';
import LinkedInSvg from '@/assets/images/channels/linkedin.svg';
import MetaSvg from '@/assets/images/channels/meta.svg';
import PinterestSvg from '@/assets/images/channels/pinterest.svg';
import SnapchatSvg from '@/assets/images/channels/snapchat.svg';
import ThreadsSvg from '@/assets/images/channels/threads.svg';
import TikTokSvg from '@/assets/images/channels/tiktok.svg';
import TwitterSvg from '@/assets/images/channels/twitter.svg';
import YoutubeSvg from '@/assets/images/channels/youtube.svg';
import FacebookNoAccessImg from '@/assets/images/facebook-no-access.png';
import InstagramNoAccessImg from '@/assets/images/instagram-no-access.png';
import CanvaSvg from '@/assets/images/integrations/canva.svg';
import LinkedinNoAccessImg from '@/assets/images/linkedin-no-access.png';
import PinterestNoAccessImg from '@/assets/images/pinterest-no-access.png';
import SnapchatConnection1x from '@/assets/images/snapchat/connection/1x.png';
import SnapchatConnection2x from '@/assets/images/snapchat/connection/2x.png';
import SnapchatConnection3x from '@/assets/images/snapchat/connection/3x.png';
import ThreadsNoAccessImg from '@/assets/images/threads-no-access.png';
import TikTokNoAccessImg from '@/assets/images/tiktok-no-access.png';
import TwitterNoAccessImg from '@/assets/images/twitter-no-access.png';
import YoutubeNoAccessImg from '@/assets/images/youtube-no-access.png';
import { BRAND, USER } from '@/models/auth/permissions.enum';

export const PLATFORM_CONNECTION_STATUS = {
  CONNECTED: 'connected',
  INVALID: 'invalid',
};

export const BRAND_PLATFORM_PERMISSIONS = Object.freeze({
  FACEBOOK: [BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS],
  FACEBOOK_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM: [BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
  INSTAGRAM_BENCHMARKS: [BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS],
  INSTAGRAM_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM_STORIES: [BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
  INSTAGRAM_UGC: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  INSTAGRAM_STORIES_UGC: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  TIKTOK_UGC: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  LINKEDIN: [BRAND.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS],
  META_ADS: [BRAND.ADS.CAN_ACCESS_ADS],
  PINTEREST: [BRAND.PINTEREST.CAN_ACCESS_PINT],
  TIKTOK: [BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS],
  TIKTOK_ADS: [BRAND.ADS.CAN_ACCESS_TIKTOK_ADS],
  TWITTER: [BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS],
  TWITTER_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  YOUTUBE: [BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS],
  YOUTUBE_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  THREADS: [BRAND.THREADS.CAN_ACCESS_THREADS_INSIGHTS],
  SNAPCHAT: [BRAND.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS],
});

export const USER_PLATFORM_PERMISSIONS = Object.freeze({
  FACEBOOK: [USER.FACEBOOK.CAN_ACCESS_FB_INSIGHTS],
  FACEBOOK_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM: [USER.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
  INSTAGRAM_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM_STORIES: [USER.INSTAGRAM.CAN_ACCESS_STORIES],
  INSTAGRAM_UGC: [USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  INSTAGRAM_STORIES_UGC: [USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  TIKTOK_UGC: [USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  LINKEDIN: [USER.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS],
  META_ADS: [USER.ADS.CAN_ACCESS_ADS],
  PINTEREST: [USER.PINTEREST.CAN_ACCESS_PINT],
  TIKTOK: [USER.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS],
  TIKTOK_ADS: [USER.ADS.CAN_ACCESS_TIKTOK_ADS],
  TWITTER: [USER.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS],
  TWITTER_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  YOUTUBE: [USER.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS],
  YOUTUBE_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  THREADS: [USER.THREADS.CAN_ACCESS_THREADS_INSIGHTS],
  SNAPCHAT: [USER.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS],
});

export const PLATFORM_CONNECTION = {
  FACEBOOK: {
    value: 'facebook',
    sandbox: 'facebook_sandbox',
    name: 'Instagram',
    img: InstagramSvg,
    SCOPES: {
      INSTAGRAM_CONTENT_PUBLISH: {
        value: 'instagram_content_publish',
      },
      INSTAGRAM_MANAGE_COMMENTS: {
        value: 'instagram_manage_comments',
      },
      INSTAGRAM_MANAGE_MESSAGES: {
        value: 'instagram_manage_messages',
      },
    },
    OPTIONAL_SCOPES: {
      INSTAGRAM_BRANDED_CONTENT_ADS_BRAND: {
        value: 'instagram_branded_content_ads_brand',
      },
    },
  },
  FACEBOOK_PAGE: {
    value: 'facebook_page',
    name: 'Facebook',
    img: FacebookSvg,
  },
  FACEBOOK_ANALYTICS: {
    value: 'facebook_analytics',
    name: 'Facebook',
    img: FacebookSvg,
  },
  // TODO sc-106472: Remove this legacy copy
  FACEBOOK_ADS: {
    value: 'facebook_ads',
    name: 'Facebook Ads',
    img: FacebookSvg,
  },
  // TODO sc-106472: Remove _new and make this the default
  FACEBOOK_ADS_NEW: {
    value: 'facebook_ads',
    name: 'Meta Ads',
    img: MetaSvg,
    iconName: 'meta',
    connectDisabledTooltip:
      'You need the “Manage Meta Ads” permission in order to connect this account.',
    disconnectDisabledTooltip:
      'You need the “Manage Meta Ads” permission in order to disconnect this account.',
  },
  INSTAGRAM_PAGE: {
    value: 'instagram_page',
    sandbox: 'instagram_page_sandbox',
    name: 'Instagram',
    img: InstagramSvg,
  },
  INSTAGRAM_INFLUENCER: {
    value: 'instagram_influencer',
  },
  PINTEREST: {
    value: 'pinterest',
    name: 'Pinterest',
    img: PinterestSvg,
  },
  PINTEREST_V5: {
    value: 'pinterest_v5',
    name: 'Pinterest',
    img: PinterestSvg,
  },
  TIKTOK: {
    value: 'tiktok',
    name: 'TikTok',
    img: TikTokSvg,
    SCOPES: {
      VIDEO_PUBLISH: {
        value: 'video.publish',
      },
    },
  },
  TIKTOK_CREATOR: {
    value: 'tiktok_creator',
    name: 'TikTok Creator',
    img: TikTokSvg,
  },
  TIKTOK_ADS: {
    value: 'tiktok_ads',
    name: 'TikTok Ads',
    img: TikTokSvg,
    connectDisabledTooltip:
      'You need the “Can Manage TikTok Ads” permission in order to connect this account.',
    disconnectDisabledTooltip:
      'You need the “Can Manage TikTok Ads” permission in order to disconnect this account.',
  },
  TIKTOK_CREATOR_MARKETPLACE: {
    value: 'tiktok_creator_marketplace',
    name: 'TikTok One Marketplace',
    img: TikTokSvg,
    connectDisabledTooltip:
      'You need the “Can Manage TikTok One Marketplace” permission in order to connect this account.',
    disconnectDisabledTooltip:
      'You need the “Can Manage TikTok One Marketplace” permission in order to disconnect this account.',
  },
  TWITTER: {
    value: 'twitter',
    name: 'X',
    img: TwitterSvg,
    SCOPES: {
      ADS_API_ACCESS: {
        value: 'ads_api_access',
      },
    },
  },
  // Outbound protocol indicator only — connections still live under the `twitter` map key.
  TWITTER_OAUTH2: {
    value: 'twitter_oauth2',
    name: 'X',
    img: TwitterSvg,
    SCOPES: {
      ADS_API_ACCESS: {
        value: 'ads_api_access',
      },
    },
  },
  YOUTUBE: {
    value: 'youtube',
    name: 'YouTube',
    img: YoutubeSvg,
  },
  GOOGLE_ANALYTICS: {
    value: 'ga',
    name: 'Google Analytics',
    img: GaSvg,
  },
  LINKEDIN: {
    value: 'linkedin',
    name: 'LinkedIn',
    img: LinkedInSvg,
    SCOPES: {
      ORGANIZATION_FOLLOWERS: {
        value: 'r_organization_followers',
      },
    },
  },
  THREADS: {
    value: 'threads',
    name: 'Threads',
    img: ThreadsSvg,
  },
  BLUESKY: {
    value: 'bluesky',
    name: 'Bluesky',
    img: BlueskySvg,
  },
  SNAPCHAT: {
    value: 'snapchat',
    name: 'Snapchat',
    img: SnapchatSvg,
  },
  CANVA: {
    value: 'canva',
    name: 'Canva',
    img: CanvaSvg,
  },
};

export const PLATFORM_CONNECTION_NAMES = {
  FACEBOOK: 'facebook_analytics',
  FACEBOOK_COMPETITIVE: 'facebook_analytics',
  INSTAGRAM: 'facebook',
  INSTAGRAM_STORY: 'facebook',
  INSTAGRAM_STORIES: 'facebook',
  INSTAGRAM_STORIES_UGC: 'facebook',
  INSTAGRAM_UGC: 'facebook',
  INSTAGRAM_COMPETITIVE: 'facebook',
  INSTAGRAM_BENCHMARKS: 'facebook',
  META_ADS: 'facebook_ads',
  YOUTUBE_COMPETITIVE: 'youtube',
  THREADS: 'threads',
  BLUESKY: 'bluesky',
  SNAPCHAT: 'snapchat',
  TIKTOK_UGC: 'tiktok',
};

// Platforms that should redirect to settings page after OAuth to display account selection popup
export const PLATFORMS_REQUIRING_SETTINGS_REDIRECT = [
  PLATFORM_CONNECTION.TIKTOK_ADS.value,
  PLATFORM_CONNECTION.FACEBOOK_ADS.value,
  PLATFORM_CONNECTION.TIKTOK_CREATOR_MARKETPLACE.value,
];

export function extractPlatformConnectionType(type, useSandbox) {
  let platformType = type?.value ?? type;

  if (platformType === 'google_analytics') {
    platformType = 'ga';
  }

  if (useSandbox) {
    const sandboxPlatformType = Object.values(PLATFORM_CONNECTION).find(
      (connection) => connection.value === platformType,
    )?.sandbox;
    return sandboxPlatformType ?? platformType;
  }

  return platformType;
}

export function isSamePlatformConnection(left, right, useSandbox) {
  const leftType = extractPlatformConnectionType(left, useSandbox);
  const rightType = extractPlatformConnectionType(right, useSandbox);
  return leftType === rightType;
}

export function determinePlatformConnectionType(type) {
  if (isObject(type)) {
    return type.value;
  }

  let result;
  if (type.includes('sandbox')) {
    result =
      Object.values(PLATFORM_CONNECTION).find((enumValue) => enumValue.sandbox === type)?.sandbox ??
      type;
  } else {
    result =
      Object.values(PLATFORM_CONNECTION).find((enumValue) => enumValue.value === type)?.value ??
      type;
  }
  return result;
}

export function extractPlatformConnectionScope(scope) {
  return scope?.value ?? scope;
}

export const PLATFORM_NO_CONNECTION_DATA = {
  INSTAGRAM: {
    name: 'Instagram',
    message: 'Essential Instagram metrics at your fingertips.',
    image: InstagramNoAccessImg,
  },
  FACEBOOK: {
    name: 'Facebook',
    message: 'Understand and connect with your fans with the right insights.',
    image: FacebookNoAccessImg,
  },
  LINKEDIN: {
    name: 'LinkedIn',
    message: 'Gain rich insight and analytics on metrics and trends.',
    image: LinkedinNoAccessImg,
  },
  TIKTOK: {
    name: 'TikTok',
    message: 'Understand how your entertaining content is performing.',
    image: TikTokNoAccessImg,
  },
  PINTEREST: {
    name: 'Pinterest',
    message: 'Make decisions based on insights that will inspire your audience.',
    image: PinterestNoAccessImg,
  },
  X: {
    name: 'X',
    message: 'Stay up to date with real-time data.',
    image: TwitterNoAccessImg,
  },
  YOUTUBE: {
    name: 'YouTube',
    message: 'Essential video-first metrics at your fingertips.',
    image: YoutubeNoAccessImg,
  },
  THREADS: {
    name: 'Threads',
    message: 'Gain rich insight and analytics on metrics and trends.',
    image: ThreadsNoAccessImg,
  },
  SNAPCHAT: {
    name: 'Snapchat',
    title: 'Snapchat Analytics',
    message:
      'Connect your Snapchat account to unlock detailed analytics. See what resonates with your audience and discover opportunities to grow.',
    bannerSrc: SnapchatConnection1x,
    bannerSrcset: [
      `${SnapchatConnection1x} 1x`,
      `${SnapchatConnection2x} 2x`,
      `${SnapchatConnection3x} 3x`,
    ],
  },
};
